import { GET, httpOk, PATCH } from 'helpers/api'
import { checkPermission, formatDate, formatMoney } from 'helpers/helpers';
import React, { useEffect, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { useTranslation } from 'react-i18next';

function OrderDetail({ modal, setModal, id }) {
    const { t } = useTranslation();

    const [data, setData] = useState([])
    const [editingItemId, setEditingItemId] = useState(null)
    const [actualValue, setActualValue] = useState('')
    const [reason, setReason] = useState('')
    const [comment, setComment] = useState('')

    const hasActualEditPermission =
        checkPermission('sales.order.actual.update') ||
        checkPermission('sales.order.items.actual.update') ||
        checkPermission('sales.order.item.actual.update') ||
        checkPermission('sales.order.actual.edit') ||
        checkPermission('sales.order.update.actual') ||
        checkPermission('inventory.finished.ship');

    function cleanNumericInput(value = '') {
        const normalized = value.replace(',', '.').replace(/[^\d.]/g, '');
        const [intPart, ...decimalParts] = normalized.split('.');
        const decimalPart = decimalParts.join('');
        return decimalPart.length > 0 ? `${intPart}.${decimalPart}` : intPart;
    }

    function resetEditState() {
        setEditingItemId(null);
        setActualValue('');
        setReason('');
        setComment('');
    }

    function startEdit(item) {
        setEditingItemId(item.id);
        setActualValue(item?.box_id ? String(item?.box_quantity || item?.quantity || '') : String(item?.quantity || ''));
        setReason('');
        setComment('');
    }

    function canEditItem(item) {
        const hasActionPermission =
            item?.allowed_actions?.includes('update_actual') ||
            item?.allowed_actions?.includes('edit_actual') ||
            data?.allowed_actions?.includes('update_actual') ||
            data?.allowed_actions?.includes('edit_actual');

        return ['processing', 'reserved', 'shipped'].includes(data?.status) && Boolean(item?.id) && (hasActualEditPermission || hasActionPermission);
    }

    function canSubmitActualValue() {
        if (!actualValue?.trim()) return false;
        return Number.isFinite(Number(actualValue));
    }

    async function updateActual(item) {
        if (!data?.id || !item?.id || !canSubmitActualValue()) return;

        const payload = item.box_id
            ? { actual_box_quantity: String(actualValue) }
            : { actual_quantity: String(actualValue) };

        if (reason.trim()) {
            payload.reason = reason.trim();
        }

        if (comment.trim()) {
            payload.comment = comment.trim();
        }

        const response = await PATCH(`/sales/orders/${data.id}/items/${item.id}/actual/`, payload);

        if (httpOk(response)) {
            setData(response.data);
            resetEditState();
        }
    }

    useEffect(() => {
        async function getData() {
            const response = await GET(`/sales/orders/${id}/`)
            if (httpOk(response)) {
                setData(response.data)
            }
        }

        if (modal && id) {
            getData()
        } else {
            setData([])
            resetEditState()
        }
    }, [modal, id]);

    return (
        <>
            <Modal show={modal} centered onHide={() => setModal(false)} dialogClassName="second">
                <i className="uil-times" onClick={() => setModal(false)}></i>
                <div className="modal-title mb-12">
                    {t('order_details')}
                </div>
                <div className="modal-subtitle mb-32">
                    {t('view_order_details')}
                </div>

                <div className="d-flex flex-column gap-5 w-50">
                    <div className="d-between">
                        <div>{t('order_number')}</div>
                        <div>{data.order_number}</div>
                    </div>
                    <div className="d-between">
                        <div>{t('distributor_name')}</div>
                        <div>{data.distributor_name}</div>
                    </div>
                    <div className="d-between">
                        <div>{t('source')}</div>
                        <div>{t(data.source)}</div>
                    </div>
                    <div className="d-between">
                        <div>{t('status')}</div>
                        <div>{t(data.status)}</div>
                    </div>
                    <div className="d-between">
                        <div>{t('total_amount')}</div>
                        <div>{formatMoney(data.total_amount)}</div>
                    </div>
                    <div className="d-between">
                        <div>{t('created_at')}</div>
                        <div>{formatDate(data.created_at)}</div>
                    </div>
                    {data.comment &&
                        <div className="d-between">
                            <div>{t('comment')}</div>
                            <div>{data.comment}</div>
                        </div>
                    }
                </div>

                <hr />

                <div className="table-responsive">
                    <table className="table">
                        <thead>
                            <tr>
                                <th style={{ width: '10%' }}>{t('box')}</th>
                                <th style={{ width: '10%' }}>{t('product_code')}</th>
                                <th style={{ width: '25%' }}>{t('name')}</th>
                                <th style={{ width: '10%' }}>{t('quantity')}</th>
                                <th style={{ width: '10%' }}>{t('unit_short')}</th>
                                <th style={{ width: '10%' }}>{t('shipped_quantity')}</th>
                                <th style={{ width: '10%' }}>{t('reserved_quantity')}</th>
                                <th style={{ width: '10%' }}>{t('unit_price')}</th>
                                <th style={{ width: '15%' }}>{t('actions')}</th>
                            </tr>
                        </thead>

                        <tbody>
                            {data?.items?.map((item, index) => (
                                <React.Fragment key={item.id || index}>
                                    <tr>
                                        <td>{item.box_name || '-'}</td>
                                        <td>{item.product_code}</td>
                                        <td>{item.product_name}</td>
                                        <td>{formatMoney(item.quantity)}</td>
                                        <td>{item.unit}</td>
                                        <td>{formatMoney(item.shipped_quantity)}</td>
                                        <td>{formatMoney(item.reserved_quantity)}</td>
                                        <td>{formatMoney(item.unit_price)}</td>
                                        <td>
                                            {canEditItem(item) &&
                                                <button className="white small" onClick={() => startEdit(item)}>
                                                    {t('edit_actual')}
                                                </button>
                                            }
                                        </td>
                                    </tr>
                                    {editingItemId === item.id &&
                                        <tr>
                                            <td colSpan={9}>
                                                <div className="d-flex flex-column gap-8">
                                                    <div className="form-group mb-0">
                                                        <label>{item.box_id ? t('actual_box_quantity') : t('actual_quantity')}</label>
                                                        <input
                                                            type="text"
                                                            value={actualValue}
                                                            onChange={(e) => setActualValue(cleanNumericInput(e.target.value))}
                                                            placeholder="0.000"
                                                        />
                                                    </div>
                                                    <div className="form-group mb-0">
                                                        <label>{t('reason')}</label>
                                                        <input
                                                            type="text"
                                                            value={reason}
                                                            onChange={(e) => setReason(e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="form-group mb-0">
                                                        <label>{t('comment')}</label>
                                                        <textarea
                                                            rows={2}
                                                            value={comment}
                                                            onChange={(e) => setComment(e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="d-flex gap-8">
                                                        <button
                                                            className="dark small"
                                                            disabled={!canSubmitActualValue()}
                                                            onClick={() => updateActual(item)}
                                                        >
                                                            {t('save')}
                                                        </button>
                                                        <button className="white small" onClick={() => resetEditState()}>
                                                            {t('cancel')}
                                                        </button>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    }
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="modal-footer">
                    <button className="white" onClick={() => setModal(false)}>
                        {t('cancel')}
                    </button>
                </div>
            </Modal>
        </>
    )
}

export default OrderDetail
